/**
 * Staggered list entrance — the SINGLE source for the whole product.
 *
 * Rules it encodes (and that the `__tests__/list-entrance.test.ts` tests
 * protect, because they break on their own):
 *
 * 1. **Once per navigation, NEVER per data refresh.** The app is
 *    local-first over PowerSync: a single mutation fires a BURST of
 *    invalidations (≈6 refetches). If the effect watched the data array,
 *    every refetch would reset the timer, keep `isEntering` true, and
 *    replay the staggered entrance → visible flicker. That's why only a
 *    derived BOOLEAN ("are there rows?") plus the `scope` is watched here.
 *
 * 2. **Staggered with a cap.** A 200-row list can't queue 200 animations:
 *    the delay saturates at {@link MAX_STAGGER_DELAY_MS}, so from around
 *    row 7 onward they all enter at once. Compositor cost is constant, not
 *    proportional to list size.
 *
 * 3. **Only `opacity` + `transform`** (see `@keyframes list-entrance` in
 *    `styles/global.css`). No `width`/`height`/`top`/`box-shadow`: nothing
 *    that forces a layout recalculation or a repaint.
 *
 * 4. **No `will-change`.** The animation lasts 200ms and starts on the same
 *    frame it mounts; permanently reserving a compositor layer on hundreds
 *    of rows costs more mobile memory than it saves.
 *
 * 5. **Never leaves a persistent opacity 0.** `animation-fill-mode: both`
 *    pins the final state (opacity 1) and, the moment the entrance ends,
 *    the hook returns `false` and the classes/styles disappear from the
 *    DOM. Playwright and screen readers see the content present and
 *    clickable from the first frame.
 */
import { type Accessor, createEffect, createMemo, createSignal, onCleanup } from "solid-js";

/** Delay between consecutive rows. */
export const STAGGER_DELAY_MS = 24;
/** Cap on the accumulated delay: past this point all rows enter together. */
export const MAX_STAGGER_DELAY_MS = 150;
/** Must match `@keyframes list-entrance`'s duration in global.css. */
export const ENTRANCE_DURATION_MS = 200;

/**
 * Scopes that ALREADY did their entrance in this navigation session. Lives
 * at the module level (not per component) so returning to an already-seen
 * list — remounting the component — doesn't animate it again.
 */
const enteredScopes = new Set<string>();

/** Tests only: clears the registry of already-animated scopes. */
export function resetListEntrance(): void {
  enteredScopes.clear();
}

/**
 * Stagger delay for row `index`, saturated at
 * {@link MAX_STAGGER_DELAY_MS}.
 */
export function entranceDelayMs(index: number): number {
  return Math.min(Math.max(index, 0) * STAGGER_DELAY_MS, MAX_STAGGER_DELAY_MS);
}

/**
 * Props to spread over each list row/card.
 *
 * When `isEntering` is `false` it returns `undefined` for both fields: no
 * animation class nor residual `style` is left in the DOM.
 */
export function listEntranceProps(
  isEntering: boolean,
  index: number,
): { class: string | undefined; style: Record<string, string> | undefined } {
  if (!isEntering) {
    return { class: undefined, style: undefined };
  }
  return {
    class: "animate-list-entrance",
    style: { "animation-delay": `${entranceDelayMs(index)}ms` },
  };
}

interface UseListEntranceOptions {
  /**
   * Identity of the list being shown (route id, cohort id, active tab…).
   * Changing it RE-ARMS the entrance; it's what tells "I navigated to
   * another list" apart from "new data arrived for the same list".
   */
  scope: Accessor<string>;
  /**
   * `true` when the list has at least one element. Must be a BOOLEAN, not
   * the array: see rule 1 in this module's header.
   */
  hasItems: Accessor<boolean>;
}

/**
 * Returns `true` while the list is doing its staggered entrance.
 *
 * @example
 * const isEntering = useListEntrance({
 *   scope: () => `cohorts:${orgId()}`,
 *   hasItems: () => (cohorts.data?.length ?? 0) > 0,
 * });
 * <For each={cohorts.data}>
 *   {(row, i) => <li {...listEntranceProps(isEntering(), i())}>…</li>}
 * </For>
 */
export function useListEntrance(options: UseListEntranceOptions): Accessor<boolean> {
  const [enteredKey, setEnteredKey] = createSignal<string | null>(null);

  // Memo over the BOOLEAN: PowerSync's refetch bursts that return "there
  // are still rows" don't propagate a change and, therefore, don't reset
  // the timer below. This memo is the heart of rule 1.
  const hasItems = createMemo(() => options.hasItems());

  createEffect(() => {
    const key = options.scope();
    if (enteredScopes.has(key)) {
      setEnteredKey(key);
      return;
    }
    if (!hasItems()) {
      return;
    }
    const timer = setTimeout(() => {
      enteredScopes.add(key);
      setEnteredKey(key);
    }, ENTRANCE_DURATION_MS + MAX_STAGGER_DELAY_MS);
    onCleanup(() => {
      clearTimeout(timer);
    });
  });

  const isEntering = createMemo(() => hasItems() && enteredKey() !== options.scope());
  return isEntering;
}
